import React, { useEffect, useRef } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Animated, { useAnimatedStyle, useSharedValue, withSpring, withTiming } from 'react-native-reanimated';
import { useKeyboardState } from 'react-native-keyboard-controller';
import MessageInput from '@/components/Connect/Conversations/MessageInput';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import useMessagesStore from '@/data/store/useMessagesStore';
import usePlatform from '@/utils/hooks/usePlatform';

const SPRING = { damping: 24, stiffness: 300, mass: 0.8 };

/**
 * ReplyContainer — floating reply UI that appears above the keyboard when
 * the user taps "Reply" on a message. Contains its own MessageInput.
 *
 * This component is rendered inside a KeyboardStickyView (in the parent screen),
 * so keyboard tracking is handled by the library. This component only handles
 * its own slide-in/out animation and auto-closing when the keyboard dismisses.
 *
 * Props:
 *   conversationID: string
 *   members: array (for mention detection)
 *   bottomInset: number
 *   onSendMessage: (text, attachments) => void  — called with repliedToID already set
 */
const ReplyContainer = ({
    conversationID,
    members,
    bottomInset = 0,
    onSendMessage,
}) => {
    const { isWeb } = usePlatform();

    const replyingToMessage = useMessagesStore((state) => state.replyingToMessage);
    const setReplyingToMessage = useMessagesStore((state) => state.setReplyingToMessage);

    const keyboardVisible = useKeyboardState((state) => state.isVisible);

    const inputRef = useRef(null);
    const keyboardWasVisible = useRef(false);
    const hasReply = !!replyingToMessage;

    //Focus the input when reply is set — use a small delay to let the TextInput mount
    useEffect(() => {
        if (replyingToMessage) {
            keyboardWasVisible.current = false;
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 250);
            return () => clearTimeout(timer);
        }
    }, [replyingToMessage]);

    //Track when keyboard becomes visible during a reply
    useEffect(() => {
        if (keyboardVisible && replyingToMessage) {
            keyboardWasVisible.current = true;
        }
    }, [keyboardVisible, replyingToMessage]);

    //Only clear reply state if keyboard was visible and then closed
    useEffect(() => {
        if (!keyboardVisible && replyingToMessage && keyboardWasVisible.current) {
            setReplyingToMessage(null);
        }
    }, [keyboardVisible, replyingToMessage]);

    //Animate in/out — slide up + fade in when reply is set, slide down + fade out when cleared.
    //Keyboard tracking is handled by the parent KeyboardStickyView.
    const opacity = useSharedValue(0);
    const slideY = useSharedValue(60);

    useEffect(() => {
        if (hasReply) {
            opacity.value = withTiming(1, { duration: 200 });
            slideY.value = withSpring(0, SPRING);
        } else {
            opacity.value = withTiming(0, { duration: 150 });
            slideY.value = withSpring(60, SPRING);
        }
    }, [hasReply]);

    const containerStyle = useAnimatedStyle(() => ({
        opacity: opacity.value,
        transform: [{ translateY: slideY.value }],
    }));

    //Don't render content when no reply — saves the MessageInput from doing work
    if (!hasReply) return null;

    return (
        <Animated.View
            style={[styles.container, !isWeb && containerStyle]}
        >
            {/* Reply preview header */}
            <View style={styles.replyHeader}>
                <View style={styles.replyHeaderLeft}>
                    <Unicon name="arrow-undo" size={16} colorVariant="textSecondary" />
                    <Text style={styles.replyingToLabel}>Replying to</Text>
                </View>
                <Pressable style={styles.closeButton} onPress={() => setReplyingToMessage(null)}>
                    <Unicon name="close" size={18} colorVariant="textSecondary" />
                </Pressable>
            </View>

            <View style={styles.replyPreview}>
                {replyingToMessage?.Sender && (
                    <Avatar size={24} profile={replyingToMessage.Sender} disableBorder />
                )}
                <View style={styles.replyPreviewContent}>
                    <Text style={styles.replyPreviewName}>
                        {replyingToMessage?.Sender?.firstName} {replyingToMessage?.Sender?.lastName}
                    </Text>
                    <Text numberOfLines={2} style={styles.replyPreviewText}>
                        {replyingToMessage?.text || (replyingToMessage?.attachments?.length > 0 ? "Attachment" : "")}
                    </Text>
                </View>
            </View>

            {/* Embedded MessageInput — hideReplyBanner since we show our own preview above */}
            <MessageInput
                inputRef={inputRef}
                hideReplyBanner
                style={styles.embeddedInput}
                detection={["mention", "link"]}
                members={members}
                placeholder="Reply to message..."
                bottomInset={bottomInset}
                attachmentDirectory={"messageAttachments/" + conversationID}
                onSendMessage={(text, attachments) => {
                    onSendMessage(text, attachments);
                    setReplyingToMessage(null);
                }}
            />
        </Animated.View>
    );
};

const styles = StyleSheet.create((theme) => ({
    container: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 60,
        backgroundColor: theme.colors.background,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
        ...(Platform.OS !== 'web' ? {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: -4 },
            shadowOpacity: 0.1,
            shadowRadius: 8,
            elevation: 8,
        } : {}),
    },
    replyHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingTop: 10,
        paddingBottom: 4,
    },
    replyHeaderLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    replyingToLabel: {
        color: theme.colors.textSecondary,
        fontSize: 12,
        fontFamily: theme.fonts.heading,
    },
    closeButton: {
        padding: 4,
    },
    replyPreview: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        paddingHorizontal: 16,
        paddingBottom: 8,
        gap: 8,
    },
    replyPreviewContent: {
        flex: 1,
    },
    replyPreviewName: {
        color: theme.colors.text,
        fontSize: 13,
        fontFamily: theme.fonts.bold,
    },
    replyPreviewText: {
        color: theme.colors.textSecondary,
        fontSize: 12,
        lineHeight: 16,
    },
    embeddedInput: {
        position: 'relative',
        bottom: 'auto',
        left: 'auto',
        right: 'auto',
        width: '100%',
        paddingBottom: 5,
    },
}));

export default React.memo(ReplyContainer);
